import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarCheck } from "lucide-react";
import { Button } from "@baluu/ui/components/button";
import { Spinner } from "@baluu/ui/components/spinner";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson, messageOf, sendJson } from "../api";
import { BookingLoading, BookingProblem, Notice, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { useAuthSnapshot } from "~/widgets/lib/auth-client";
import { CustomerAuthDialog } from "../../CustomerAuthDialog/CustomerAuthDialog";
import { BackToBooking, ClassSummary } from "./reschedule/FlowParts";
import { SessionPicker } from "./reschedule/SessionPicker";
import { AppointmentPicker, type AppointmentChoice } from "./reschedule/AppointmentPicker";
import { authInit } from "./reschedule/request";
import { rescheduleErrorCopy } from "./reschedule/rescheduleErrors";
import {
  cancelBoxMessage,
  listingTitle,
  offersListingSwitch,
  reschedulePayload,
  type CrossListing,
} from "./reschedule/rescheduleState";
import { formatLongDate, formatSessionDateTime, sessionLocationLabel, sessionTimezone, toSessionRow } from "./reschedule/sessions";
import { viewerTimezone } from "../timezones";
import type { ApiBooking, ApiCourse, ApiTicketType, LocationLike, SessionLike, TutorLike } from "./reschedule/apiTypes";

// /bookings/reschedule/<id>: move a booking to another date. A port of v1's
// reschedule page (BookingRescheduleClient + panel/*): a regular booking
// picks another session, optionally on another listing the school allows; an
// appointment booking picks another day and time; a booking that can't be
// moved says why (ErrorCodeView). Under the dates, the way to cancel instead.

type ReschedulePage = {
  errorCode?: string;
  error?: string;
  booking?: ApiBooking;
  canCancel?: boolean;
  course?: ApiCourse;
  crossListing?: CrossListing | null;
  locations?: LocationLike[];
  session?: SessionLike;
  sessions?: SessionLike[];
  tutors?: TutorLike[];
  ticketTypes?: ApiTicketType[];
};
type LoadedPage = ReschedulePage & { booking: ApiBooking; course: ApiCourse };

type ListingSessions = { sessions?: SessionLike[]; locations?: LocationLike[]; errorCode?: string; error?: string };

type Done = { startDateTime: string; duration?: number; isOnline?: boolean; location?: string; listing?: string };

export function RescheduleView({ ctx, bookingId }: { ctx: ViewContext; bookingId: string }) {
  const auth = useAuthSnapshot();
  const token = auth.token;
  const qc = useQueryClient();
  const [signInOpen, setSignInOpen] = useState(false);

  const page = useQuery({
    queryKey: ["booking", "reschedule", ctx.api, bookingId, token],
    queryFn: () =>
      bookingJson<ReschedulePage>(`${ctx.api}/widget/pages/bookings/reschedule/${encodeURIComponent(bookingId)}`, authInit(token)),
    retry: (count, error) => !(error instanceof BookingApiError && error.status < 500) && count < 2,
  });

  const elsewhere = !!page.data && isOtherSchool(ctx.businessId, page.data.booking?.school, page.data.course?.schoolId);
  const isMarketplace = !elsewhere && page.data?.booking?.source === "obby";
  useEffect(() => {
    if (isMarketplace) ctx.toMarketplace(`/bookings/reschedule/${bookingId}`);
  }, [isMarketplace, ctx, bookingId]);

  const back = <BackToBooking href={ctx.links.manage(bookingId)} />;
  const terms = ctx.links.terms();

  if (page.isLoading || isMarketplace) return <BookingLoading rows={4} />;
  if (page.isError) {
    const notFound = page.error instanceof BookingApiError && page.error.status === 404;
    return notFound ? (
      <RecordNotFound />
    ) : (
      <BookingProblem
        title="We couldn’t load this booking"
        message={messageOf(page.error, "Please try again in a moment.")}
        onRetry={() => page.refetch()}
      />
    );
  }

  if (elsewhere) return <RecordNotFound />;
  const data = page.data!;
  if (data.errorCode || !data.booking || !data.course) {
    const copy = rescheduleErrorCopy(data.errorCode ?? "", data.error);
    return (
      <div className="space-y-4">
        {back}
        <BookingProblem
          title={copy.title}
          message={copy.message}
          action={
            copy.action === "sign-in" ? (
              <Button size="sm" onClick={() => setSignInOpen(true)}>
                Sign in or sign up
              </Button>
            ) : copy.action === "terms" ? (
              <a className="text-sm font-medium text-primary underline underline-offset-4" href={terms}>
                Read our terms of booking
              </a>
            ) : undefined
          }
        />
        <CustomerAuthDialog
          open={signInOpen}
          onOpenChange={setSignInOpen}
          apiUrl={ctx.api}
          businessSlug={ctx.businessSlug}
          primaryColor={ctx.primaryColor}
          onSignedIn={() => {
            setSignInOpen(false);
            qc.invalidateQueries({ queryKey: ["booking", "reschedule"] });
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {back}
      <RescheduleForm ctx={ctx} bookingId={bookingId} data={data as LoadedPage} token={token} terms={terms} />
    </div>
  );
}

function RescheduleForm({
  ctx,
  bookingId,
  data,
  token,
  terms,
}: {
  ctx: ViewContext;
  bookingId: string;
  data: LoadedPage;
  token: string | null;
  terms: string;
}) {
  const booking = data.booking;
  const course = data.course;
  const qc = useQueryClient();
  const ownListingId = String(course._id);
  const bookingTimezone: string = booking.timezone || course.timezone;
  const isAppointment = booking.bookingType === "anytime";
  const isRegular = booking.bookingType === "regular";
  const viewer = viewerTimezone();

  const [listingId, setListingId] = useState(ownListingId);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [appointment, setAppointment] = useState<AppointmentChoice>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  const onOtherListing = listingId !== ownListingId;
  const listingQuery = (id: string) => ({
    queryKey: ["booking", "reschedule-listing", ctx.api, bookingId, id, token],
    queryFn: async () => {
      const res = await bookingJson<ListingSessions>(
        `${ctx.api}/widget/pages/bookings/reschedule/${encodeURIComponent(bookingId)}/listings/${encodeURIComponent(id)}/sessions`,
        authInit(token),
      );
      if (res.errorCode) throw new BookingApiError(200, rescheduleErrorCopy(res.errorCode, res.error).message, res.errorCode);
      return res;
    },
    retry: false,
    staleTime: 60_000,
  });
  const other = useQuery({ ...listingQuery(listingId), enabled: onOtherListing });

  // Another listing's dates load when it's picked; when they can't, say so
  // and go back to the booking's own (v1 did the same).
  async function pickListing(id: string) {
    setListingId(id);
    setSessionId(undefined);
    setProblem(null);
    if (id === ownListingId) return;
    try {
      await qc.fetchQuery(listingQuery(id));
    } catch (error) {
      setProblem(
        error instanceof BookingApiError && error.status === 200 ? error.message : "We couldn’t load dates for that class. Please try again.",
      );
      setListingId(ownListingId);
    }
  }

  const sessions = useMemo(() => (onOtherListing ? other.data?.sessions : data.sessions) ?? [], [onOtherListing, other.data, data.sessions]);
  const locations = useMemo(
    () => (onOtherListing ? other.data?.locations : data.locations) ?? [],
    [onOtherListing, other.data, data.locations],
  );
  const isLoadingSessions = onOtherListing && other.isLoading;
  const selectedSession = sessions.find((s) => s._id === sessionId);

  const schoolId = typeof booking.school === "string" ? booking.school : (booking.school?._id ?? "");
  const rows = useMemo(
    () =>
      sessions.map((s) =>
        toSessionRow(s, { timezone: bookingTimezone, locations, tutors: data.tutors, schoolId, viewer }),
      ),
    [sessions, locations, data.tutors, bookingTimezone, schoolId, viewer],
  );

  const choice: { sessionId?: string; startDateTime: string; duration?: number } | null = isAppointment
    ? appointment
    : selectedSession
      ? { sessionId: selectedSession._id, startDateTime: selectedSession.startDateTime, duration: selectedSession.duration }
      : null;

  const submit = useMutation({
    mutationFn: (body: ReturnType<typeof reschedulePayload>) =>
      sendJson<{ ok?: string }>(`${ctx.api}/widget/pages/bookings/reschedule/${encodeURIComponent(bookingId)}`, "POST", body, authInit(token)),
    onSuccess: () => {
      setDone({
        startDateTime: choice!.startDateTime,
        duration: choice!.duration,
        isOnline: isAppointment ? course.isOnline : selectedSession?.isOnline,
        location: selectedSession ? sessionLocationLabel(selectedSession, locations) : undefined,
        listing: onOtherListing ? listingTitle(data.crossListing, listingId) : undefined,
      });
      // Other views of this booking are stale now; this page keeps its
      // own answer so the confirmation stays on screen.
      qc.invalidateQueries({ queryKey: ["booking"], predicate: (q) => q.queryKey[1] !== "reschedule" });
    },
    onError: (error) => setProblem(messageOf(error, "We couldn’t reschedule your booking. Please try again.")),
  });

  if (done) {
    const zone = sessionTimezone({ isOnline: done.isOnline }, bookingTimezone, viewer);
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="space-y-3 rounded-lg border bg-card p-6 text-center">
          <CalendarCheck className="mx-auto size-10 text-primary" aria-hidden="true" />
          <h1 className="text-2xl font-semibold">You’ve rescheduled your class</h1>
          <Notice tone="success">
            {done.listing && (
              <>
                New class: {done.listing}
                <br />
              </>
            )}
            New date:{" "}
            {formatSessionDateTime({ _id: "", startDateTime: done.startDateTime, duration: done.duration, isOnline: done.isOnline }, zone, viewer)}
            {done.location && `, ${done.location}`}
          </Notice>
          <p className="text-sm text-muted-foreground">We’ve emailed you the new details.</p>
          <a
            href={ctx.links.manage(bookingId)}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
          >
            View your booking
          </a>
        </div>
      </div>
    );
  }

  const isSubmitting = submit.isPending;
  const cancelMessage = cancelBoxMessage({
    bookingType: booking.bookingType,
    sessionsCount: sessions.length,
    isLoadingSessions,
    onOtherListing,
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-semibold">Reschedule your booking</h1>
      <ClassSummary title={course.title} thumbnail={course.thumbnail}>
        {data.session && <>Currently: {formatSessionDateTime(data.session, bookingTimezone, viewer)}</>}
      </ClassSummary>

      {problem && (
        <div className="space-y-2">
          <Notice tone="error">
            <p className="font-medium">Unable to reschedule</p>
            <p>{problem}</p>
          </Notice>
          <Button variant="ghost" size="sm" onClick={() => setProblem(null)}>
            Dismiss
          </Button>
        </div>
      )}

      {isAppointment && (
        <AppointmentPicker
          api={ctx.api}
          course={course}
          ticketTypes={data.ticketTypes ?? []}
          currency={booking.currencyUsed}
          disabled={isSubmitting}
          onChange={setAppointment}
        />
      )}

      {isRegular && offersListingSwitch(booking.bookingType, data.crossListing) && (
        <div className="space-y-2">
          <label htmlFor="reschedule-listing" className="block text-sm font-medium">
            Show dates for
          </label>
          <select
            id="reschedule-listing"
            className="h-10 w-full rounded-md border bg-background px-3 text-sm disabled:cursor-not-allowed disabled:opacity-60"
            value={listingId}
            disabled={isSubmitting || isLoadingSessions}
            onChange={(e) => void pickListing(e.target.value)}
          >
            <option value={ownListingId}>{course.title} (current)</option>
            {data.crossListing!.listings.map((listing) => (
              <option key={listing._id} value={listing._id}>
                {listing.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {isRegular && isLoadingSessions && <BookingLoading rows={2} />}
      {isRegular && !isLoadingSessions && rows.length > 0 && (
        <SessionPicker
          rows={rows}
          timezone={bookingTimezone}
          selected={sessionId ? [sessionId] : []}
          isDisabled={(row) => isSubmitting || row.soldOut}
          onToggle={(id) => setSessionId((current) => (current === id ? undefined : id))}
        />
      )}

      <div className="space-y-3 rounded-lg border bg-muted/30 p-6 text-center">
        {data.canCancel ? (
          <>
            {cancelMessage && <p className="text-sm">{cancelMessage}</p>}
            <a
              href={ctx.links.cancel(bookingId)}
              className="inline-flex h-9 items-center justify-center rounded-md border bg-background px-4 text-sm font-medium hover:bg-muted"
            >
              Cancel booking
            </a>
          </>
        ) : (
          <p className="text-sm">
            Our{" "}
            <a href={terms} className="font-medium text-primary underline underline-offset-4">
              cancellation policy
            </a>{" "}
            doesn’t allow this booking to be cancelled.
          </p>
        )}
      </div>

      <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-background/95 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm">
          <span className="text-muted-foreground">Date selected: </span>
          <span className="font-medium">{choice ? formatLongDate(choice.startDateTime, sessionTimezone({ isOnline: isAppointment ? course.isOnline : selectedSession?.isOnline }, bookingTimezone, viewer)) : "None yet"}</span>
        </p>
        <Button
          disabled={!choice || isSubmitting}
          onClick={() => {
            if (!choice) return;
            setProblem(null);
            submit.mutate(reschedulePayload({ sessionId: choice.sessionId, startDateTime: choice.startDateTime, listingId, ownListingId }));
          }}
        >
          {isSubmitting && <Spinner className="mr-2 size-4" />}
          Reschedule
        </Button>
      </div>
    </div>
  );
}
